"use client";

import { useEffect, useRef, useState } from "react";
import CyberBoxChallengs from "../../../../shared/ui/layout/CyberBoxChallengs";
import { motion } from "motion/react";
import { SparklesCore } from "../../../../shared/animations/sparkles/soarklesCore";

const ACTION_IDS = ["SHOW_CHALLENGES", "SHOW_DESCRIPTION"];

function TypewriterText({
  text,
  speed = 40,
  delay = 150,
  loop = false,
  className = "",
  cursor = true,
  dir = "rtl",
}) {
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!text) return;
    let startT, stepI, resetT;
    const start = () => {
      stepI = setInterval(() => {
        setI((prev) => {
          if (prev < text.length) return prev + 1;
          if (loop) {
            clearInterval(stepI);
            resetT = setTimeout(() => {
              setI(0);
              start();
            }, 900);
          }
          return prev;
        });
      }, speed);
    };
    startT = setTimeout(start, delay);
    return () => {
      clearTimeout(startT);
      clearInterval(stepI);
      clearTimeout(resetT);
    };
  }, [text, speed, delay, loop]);

  const shown = text.slice(0, i);
  const done = i >= text.length && !loop;

  return (
    <p dir={dir} className={className} aria-label={text}>
      {shown}
      {cursor && !done && (
        <motion.span
          className="inline-block align-baseline"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
          style={{
            width: "0.06em",
            height: "0.9em",
            marginInlineStart: "0.15em",
            backgroundColor: "#ffffff",
            borderRadius: 2,
          }}
        />
      )}
    </p>
  );
}

const Challenges = () => {
  const [data, setData] = useState({ title: "", description: "" });
  const bcRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const payload = e?.detail ?? {};
      if (payload?.title || payload?.description) {
        setData({
          title: payload.title ?? "",
          description: payload.description ?? "",
        });
      }
    };

    ACTION_IDS.forEach((id) => window.addEventListener(id, handler));

    if (typeof window !== "undefined") {
      bcRef.current = new BroadcastChannel("operator-events");
      bcRef.current.onmessage = (msg) => {
        const { actionId, payload } = msg?.data || {};
        if (!ACTION_IDS.includes(actionId)) return;
        if (payload?.title || payload?.description) {
          setData({
            title: payload.title ?? "",
            description: payload.description ?? "",
          });
        }
      };
    }

    return () => {
      ACTION_IDS.forEach((id) => window.removeEventListener(id, handler));
      if (bcRef.current) bcRef.current.close();
    };
  }, []);

  const hasData =
    (data.title && data.title.trim()) ||
    (data.description && data.description.trim());

  if (!hasData)
    return (
      <CyberBoxChallengs>
        <div className="flex items-center justify-center text-white text-2xl h-full">
          منتظر پیام چالش…
        </div>
      </CyberBoxChallengs>
    );

  const descLength = data.description?.length ?? 0;
  const descSpeed =
    descLength > 1200
      ? 4
      : descLength > 800
      ? 8
      : descLength > 400
      ? 15
      : descLength > 200
      ? 25
      : 35;

  return (
    <CyberBoxChallengs>
      <div className="relative w-full h-full text-white flex flex-col items-center justify-center gap-[24px]">
        <SparklesCore
          id="tsparticles-challenges"
          background="transparent"
          minSize={0.6}
          maxSize={1.4}
          particleDensity={40}
          className="w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden"
          particleColor="#E8232C"
        />

        <div
          style={{
            backgroundImage:
              "radial-gradient(ellipse 60% 35% at 50% 50%, rgba(240,48,0,0.48) -25%, rgba(240,48,0,0) 70%)",
            borderRadius: "16px",
          }}
          className="w-full h-full absolute z-[3] pointer-events-none"
        />

        <div className="w-full max-w-[50%] flex flex-col items-center justify-center overflow-hidden rounded-md relative z-30 px-4 mt-[100px]">
          <TypewriterText
            key={data.title}
            text={data.title}
            speed={30}
            delay={150}
            loop={false}
            cursor={true}
            className="text-[#FFFFFF] text-[84px] font-[900] text-center leading-snug"
          />

          {data.description && (
            <TypewriterText
              key={data.description}
              text={data.description}
              speed={descSpeed}
              delay={data.title?.length * 25 + 400}
              loop={false}
              cursor={false}
              className="text-[#EAEAEA] text-[54px] font-[400] text-center leading-snug mt-6 opacity-90 whitespace-pre-wrap"
            />
          )}

          <div className="relative mt-12 h-40 w-[min(40rem,90vw)]">
            <div className="absolute inset-x-20 top-0 h-[3px] w-3/4 bg-gradient-to-r from-transparent via-white/70 to-transparent blur-md" />
            <div className="absolute inset-x-20 top-0 h-px w-3/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_8px_#ffffff]" />
            <div className="absolute inset-x-60 top-0 h-[6px] w-1/4 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-md" />
            <div className="absolute inset-x-60 top-0 h-px w-1/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_10px_#ffffff]" />
            <div className="absolute inset-x-44 top-0 h-[2px] w-2/4 bg-gradient-to-r from-transparent via-sky-100/60 to-transparent blur-[2px]" />
          </div>
        </div>
      </div>
    </CyberBoxChallengs>
  );
};

export default Challenges;
